import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TimelineTracker from '../components/TimelineTracker';
import CollegeTracker from '../components/CollegeTracker';
import StatsOverview from '../components/StatsOverview';
import ScholarshipDeadlines from '../components/ScholarshipDeadlines';
import FeatureGate from '../components/FeatureGate';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getQuoteOfTheDay } from '../data/motivationalQuotes';

const {
  FiEdit3, FiMenu, FiX, FiRefreshCw, FiBookOpen, FiChevronDown, FiChevronUp,
  FiExternalLink, FiInfo, FiAward, FiUsers, FiGlobe, FiCheck, FiLock,
  FiGrid, FiCalendar, FiHeart, FiClipboard
} = FiIcons;

const GOVERNING_BODIES = [
  { id: 'ncaa', name: 'NCAA', fullName: 'National Collegiate Athletic Association', website: 'https://www.ncaa.org', eligibilityUrl: 'https://web3.ncaa.org/ecwr3/', color: 'blue', founded: '1906', memberSchools: '1,100+', studentAthletes: '500,000+', overview: 'The NCAA is the largest and most prominent governing body for college athletics in the United States, overseeing three competitive divisions with varying levels of scholarships and athletic commitments.', divisions: [{ name: 'Division I', desc: 'Highest level with the most scholarships; includes FBS and FCS football programs. Schools must offer at least 7 sports for men and 7 for women.' }, { name: 'Division II', desc: 'Mid-level with partial scholarships available. Balances athletics and academics with fewer time demands than D-I.' }, { name: 'Division III', desc: 'No athletic scholarships but strong academic focus. Athletes receive need-based and merit financial aid.' }], eligibility: ['Register with the NCAA Eligibility Center (formerly Clearinghouse)', 'Complete 16 core courses in high school for D-I and D-II', 'Meet minimum GPA requirements on a sliding scale with test scores', 'Maintain amateur status — avoid pay-for-play situations', 'D-I athletes have 4 years of eligibility within a 5-year window'], scholarshipInfo: 'D-I offers full and partial scholarships (equivalency sports split aid among multiple athletes; head-count sports give full rides). D-II offers partial scholarships. D-III offers no athletic scholarships.', keyFacts: ['Largest governing body in U.S. college athletics', 'Manages the NCAA Tournament and major bowl games', 'Strict recruiting contact rules by sport and division', 'National Letter of Intent (NLI) signed during signing periods'] },
  { id: 'naia', name: 'NAIA', fullName: 'National Association of Intercollegiate Athletics', website: 'https://www.naia.org', eligibilityUrl: 'https://www.naia.org/eligibility', color: 'green', founded: '1937', memberSchools: '250+', studentAthletes: '65,000+', overview: 'The NAIA governs smaller colleges and universities with a strong emphasis on character-driven athletics. It operates independently from the NCAA and has its own eligibility rules, offering a competitive yet more personalized athletic experience.', divisions: [{ name: 'NAIA (Single Division)', desc: 'One unified competitive level. Schools tend to be smaller and offer a tight-knit community environment with strong athletics programs.' }], eligibility: ['Register with the NAIA Eligibility Center', 'Meet 2 of 3 entry-level requirements: GPA ≥ 2.0, ACT ≥ 18 or SAT ≥ 970, graduate in top half of class', 'Complete high school with a standard diploma or GED', 'Maintain a 2.0 GPA each term in college', 'Have not completed 4 seasons of a sport'], scholarshipInfo: 'NAIA schools can offer athletic scholarships without the strict equivalency limits found in NCAA D-I. Many schools offer competitive aid packages that can cover tuition, room, and board.', keyFacts: ['More flexible eligibility rules than NCAA', 'Separate championship events from NCAA', 'Strong scholarship opportunities at smaller schools', 'Focus on "Champions of Character" program'] },
  { id: 'njcaa', name: 'NJCAA', fullName: 'National Junior College Athletic Association', website: 'https://www.njcaa.org', eligibilityUrl: 'https://www.njcaa.org/eligibility/index', color: 'purple', founded: '1937', memberSchools: '500+', studentAthletes: '60,000+', overview: 'The NJCAA governs two-year community and junior colleges across the United States. It serves as a critical pathway for athletes who want to develop their skills, improve academics, and ultimately transfer to 4-year universities.', divisions: [{ name: 'Division I', desc: 'Full athletic scholarships allowed including tuition, fees, room, board, and books.' }, { name: 'Division II', desc: 'Partial scholarships covering tuition and fees only.' }, { name: 'Division III', desc: 'No athletic scholarships; athletes may receive academic or need-based aid.' }], eligibility: ['Must be a high school graduate or GED holder', 'Must be enrolled in at least 12 credit hours per semester', 'First-year students are immediately eligible', 'Maximum of 2 years of eligibility at a junior college', 'Must maintain satisfactory academic progress (SAP)'], scholarshipInfo: 'D-I NJCAA schools can offer full scholarships covering all expenses. D-II offers tuition and fees only. D-III offers no athletic scholarships but students may receive institutional aid.', keyFacts: ['Excellent pathway to 4-year NCAA or NAIA programs', 'Immediate eligibility for first-year students', 'Over 500 member colleges across the U.S.', 'Strong transfer pipelines to D-I and D-II programs'] },
  { id: 'cccaa', name: 'CCCAA', fullName: 'California Community College Athletic Association', website: 'https://www.cccaasports.org', eligibilityUrl: 'https://www.cccaasports.org/eligibility', color: 'yellow', founded: '1927', memberSchools: '100+', studentAthletes: '25,000+', overview: 'The CCCAA governs athletics for California community colleges, providing one of the strongest pipeline systems to 4-year universities including the UC and CSU systems. It is one of the largest community college athletic associations in the country.', divisions: [{ name: 'Community College (Single Level)', desc: "All California community colleges compete at one level within regional conferences. Competition is highly competitive given California's talent pool." }], eligibility: ['Must be enrolled in at least 12 units per semester', 'Must maintain a 2.0 GPA (C average) each semester', 'Must have a valid high school diploma or GED', 'Limited to 2 years of athletic eligibility per sport', 'Must make progress toward an AA/AS degree or transfer goal'], scholarshipInfo: 'California community colleges are part of the state system and typically do not offer traditional athletic scholarships. However, athletes may qualify for Board of Governors Fee Waivers and other state/federal financial aid, effectively reducing costs significantly.', keyFacts: ['One of the largest community college athletic associations in the U.S.', 'Strong pipeline to UC and CSU 4-year programs', 'Great opportunity for late-blooming or academically recovering athletes', 'Highly competitive talent pool from California high schools'] },
  { id: 'nccaa', name: 'NCCAA', fullName: 'National Christian College Athletic Association', website: 'https://thenccaa.org', eligibilityUrl: 'https://thenccaa.org/eligibility', color: 'orange', founded: '1968', memberSchools: '100+', studentAthletes: '12,000+', overview: 'The NCCAA is a faith-based athletic association that governs Christian colleges and universities. Many member schools are also members of the NCAA or NAIA, allowing athletes to compete in both associations simultaneously.', divisions: [{ name: 'Division I', desc: 'Larger Christian universities, many of which are also NCAA D-II or NAIA members.' }, { name: 'Division II', desc: 'Smaller Bible colleges and Christian institutions with limited athletic budgets.' }], eligibility: ['Must be enrolled at an NCCAA member institution', 'Must be a professing Christian (statement of faith may be required)', 'Follow the eligibility rules of the primary association (NCAA/NAIA) if dually affiliated', 'Maintain satisfactory academic progress', 'Standard amateur status requirements apply'], scholarshipInfo: "Scholarship availability varies by institution. Since many NCCAA schools are also NCAA or NAIA members, scholarship limits follow those associations' rules. Some schools offer faith-based institutional aid in addition to athletic scholarships.", keyFacts: ['Faith-integrated athletic competition and community', 'Many schools hold dual membership with NCAA or NAIA', 'Athletes can compete in both NCCAA and their primary association championships', 'Strong emphasis on spiritual development alongside athletics'] },
  { id: 'uscaa', name: 'USCAA', fullName: 'United States Collegiate Athletic Association', website: 'https://theuscaa.com', eligibilityUrl: 'https://theuscaa.com/Recruit_Portal/Portal_Homepage', eligibilityLabel: 'Recruit Portal', color: 'red', founded: '1999', memberSchools: '100+', studentAthletes: '10,000+', overview: 'The USCAA serves very small colleges and universities that may not meet the membership requirements of larger associations like the NCAA or NAIA. It provides a competitive athletic framework for institutions with limited resources.', divisions: [{ name: 'Division I', desc: 'Slightly larger small colleges with more developed athletic programs.' }, { name: 'Division II', desc: 'Very small institutions with limited athletic budgets and rosters.' }], eligibility: ['Must be enrolled full-time at a USCAA member institution', 'Must maintain a 2.0 cumulative GPA', 'Must be making satisfactory academic progress toward a degree', 'Standard amateur status requirements apply', 'Transfer rules follow USCAA guidelines'], scholarshipInfo: 'Athletic scholarships are rare in the USCAA due to the small size of member institutions. Students may receive institutional, need-based, or merit aid. The focus is on providing competitive opportunities for athletes at very small schools.', keyFacts: ['Serves very small colleges not eligible for NCAA or NAIA', 'Provides championship opportunities across multiple sports', 'Lower financial barrier to entry for member schools', 'Good option for athletes who want to compete at the college level at smaller institutions'] },
];

const colorConfig = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', icon: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  green: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', icon: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', icon: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', icon: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', icon: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(getQuoteOfTheDay());
  const [openBodies, setOpenBodies] = useState(new Set());
  const { user } = useAuth();
  const { getUserData } = useUserData();
  const { canAccessPremiumFeatures } = useSubscription();
  const profile = getUserData('profile') || {};
  const firstName = profile.firstName || 'Athlete';
  const hasAccess = canAccessPremiumFeatures();

  const tabs = [
    { id: 'overview',  label: 'Overview',    icon: FiGrid,      requiresAccess: false },
    { id: 'timeline',  label: 'Timeline',    icon: FiCalendar,  requiresAccess: true },
    { id: 'colleges',  label: 'Colleges',    icon: FiHeart,     requiresAccess: true },
    { id: 'deadlines', label: 'Deadlines',   icon: FiClipboard, requiresAccess: true },
    { id: 'bodies',    label: 'Gov. Bodies', icon: FiBookOpen,  requiresAccess: true },
  ];

  useEffect(() => { setCurrentQuote(getQuoteOfTheDay()); }, []);

  const handleRefreshQuote = () => setCurrentQuote(getQuoteOfTheDay());
  const handleEditProfile = () => navigate('/settings');

  const handleTabClick = (tab) => {
    if (tab.requiresAccess && !hasAccess) {
      navigate('/settings?tab=subscription');
    } else {
      setActiveTab(tab.id);
    }
    setShowMobileMenu(false);
  };

  const toggleBody = (id) => {
    setOpenBodies((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const activeTabData = tabs.find((t) => t.id === activeTab);
  const activeTabLabel = activeTabData?.label || 'Overview';
  const activeTabIcon = activeTabData?.icon;
  const activeTabLocked = activeTabData?.requiresAccess && !hasAccess;

  const renderGoverningBodiesContent = () => (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-space-grotesk font-semibold text-secondary">Athletic Governing Bodies</h2>
          <p className="text-gray-500 text-sm">Detailed breakdown of all major organizations overseeing college athletics</p>
        </div>
      </div>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-700">Each governing body has its own eligibility rules, scholarship limits, and recruiting timelines. Click any body below to expand full details including eligibility requirements, divisions, and scholarship information.</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 text-center">
          <SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary mx-auto mb-1" />
          <div className="text-xl font-bold text-secondary">6</div>
          <div className="text-xs text-gray-500">Governing Bodies</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 text-center">
          <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600 mx-auto mb-1" />
          <div className="text-xl font-bold text-secondary">2,000+</div>
          <div className="text-xs text-gray-500">Member Schools</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 text-center">
          <SafeIcon icon={FiAward} className="w-6 h-6 text-yellow-600 mx-auto mb-1" />
          <div className="text-xl font-bold text-secondary">700k+</div>
          <div className="text-xs text-gray-500">Student Athletes</div>
        </div>
      </div>
      <div className="space-y-3">
        {GOVERNING_BODIES.map((body, index) => {
          const colors = colorConfig[body.color];
          const isOpen = openBodies.has(body.id);
          return (
            <motion.div key={body.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: index * 0.06 }} className={`bg-white rounded-xl border ${colors.border} shadow-sm overflow-hidden`}>
              <button onClick={() => toggleBody(body.id)} className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 ${colors.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-lg font-bold">{body.name[0]}</span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <h3 className="text-base font-space-grotesk font-semibold text-secondary">{body.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${colors.badge}`}>Est. {body.founded}</span>
                    </div>
                    <p className="text-xs text-gray-500">{body.fullName}</p>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-xs text-gray-500">{body.memberSchools} schools</span>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500">{body.studentAthletes} athletes</span>
                    </div>
                  </div>
                </div>
                <SafeIcon icon={isOpen ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div key="content" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                    <div className="px-5 pb-6 border-t border-gray-100 space-y-5">
                      <div className="pt-4"><p className="text-sm text-gray-700 leading-relaxed">{body.overview}</p></div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary mb-2 flex items-center space-x-2">
                          <SafeIcon icon={FiGlobe} className="w-4 h-4 text-primary" />
                          <span>Divisions / Levels</span>
                        </h4>
                        <div className="space-y-2">
                          {body.divisions.map((div, i) => (
                            <div key={i} className={`${colors.bg} rounded-lg p-3`}>
                              <p className="text-xs font-semibold text-secondary mb-0.5">{div.name}</p>
                              <p className="text-xs text-gray-600">{div.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary mb-2 flex items-center space-x-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                          <span>Eligibility Requirements</span>
                        </h4>
                        <ul className="space-y-1.5">
                          {body.eligibility.map((req, i) => (
                            <li key={i} className="flex items-start space-x-2 text-sm text-gray-700">
                              <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${colors.dot}`} />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary mb-2 flex items-center space-x-2">
                          <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-600" />
                          <span>Scholarship Information</span>
                        </h4>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-sm text-gray-700">{body.scholarshipInfo}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary mb-2 flex items-center space-x-2">
                          <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-600" />
                          <span>Key Facts</span>
                        </h4>
                        <ul className="grid sm:grid-cols-2 gap-2">
                          {body.keyFacts.map((fact, i) => (
                            <li key={i} className="flex items-start space-x-2 text-xs text-gray-700 bg-gray-50 rounded-lg p-2">
                              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${colors.dot}`} />
                              <span>{fact}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-3 pt-1">
                        <a href={body.website} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center space-x-2 text-sm font-medium px-4 py-2 rounded-lg ${colors.badge} hover:opacity-80 transition-opacity`}>
                          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                          <span>Official Website</span>
                        </a>
                        <a href={body.eligibilityUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-sm font-medium px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                          <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          <span>{body.eligibilityLabel || 'Eligibility Center'}</span>
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <p className="text-sm text-gray-600"><strong>Pro Tip:</strong> Many athletes explore multiple governing bodies simultaneously. For example, NCCAA schools often hold dual membership with the NCAA or NAIA, letting you compete in both associations. Always verify specific rules directly with each school's athletic department.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col space-y-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-space-grotesk font-bold text-secondary mb-2">
                Welcome back, {firstName}!
              </h1>
              <p className="text-sm sm:text-base text-gray-600">Track your progress and manage your athletic scholarship journey.</p>
            </div>
            <div className="flex justify-end">
              <button onClick={handleEditProfile} className="bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2 text-sm">
                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quote Card */}
        <div className="mb-6 sm:mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-primary to-blue-700 text-white rounded-xl p-6 sm:p-8 shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-blue-200 mb-3">Quote of the Day</div>
                <blockquote className="text-lg sm:text-xl md:text-2xl font-medium leading-relaxed mb-4">"{currentQuote.quote}"</blockquote>
                <cite className="text-sm sm:text-base text-blue-200 not-italic">— {currentQuote.author}</cite>
              </div>
              <button onClick={handleRefreshQuote} className="ml-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0" title="New quote">
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            <div className="pt-4 border-t border-white/20">
              <p className="text-xs sm:text-sm text-blue-100">Stay motivated and focused on your goals. Every day is a new opportunity to get closer to your dreams.</p>
            </div>
          </motion.div>
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden sm:block border-b border-gray-200 mb-6 sm:mb-8">
          <nav className="flex space-x-1 sm:space-x-4 overflow-x-auto">
            {tabs.map((tab) => {
              const locked = tab.requiresAccess && !hasAccess;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center space-x-1.5 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : locked
                      ? 'border-transparent text-gray-400 cursor-not-allowed'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {locked && <SafeIcon icon={FiLock} className="w-3 h-3 ml-0.5" />}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Mobile Tab Navigation - Dropdown */}
        <div className="sm:hidden mb-6">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between text-left shadow-sm"
          >
            <div className="flex items-center space-x-2">
              {activeTabIcon && (
                <SafeIcon icon={activeTabIcon} className="w-4 h-4 text-primary" />
              )}
              <span className="font-medium text-secondary">{activeTabLabel}</span>
              {activeTabLocked && <SafeIcon icon={FiLock} className="w-3 h-3 text-gray-400" />}
            </div>
            <SafeIcon icon={showMobileMenu ? FiX : FiMenu} className="w-5 h-5 text-gray-400" />
          </button>

          <AnimatePresence>
            {showMobileMenu && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10 relative"
              >
                {tabs.map((tab) => {
                  const locked = tab.requiresAccess && !hasAccess;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab)}
                      className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between border-b border-gray-50 last:border-b-0 ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-primary font-medium'
                          : locked
                          ? 'text-gray-400'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={tab.icon} className="w-4 h-4" />
                        <span className="text-sm">{tab.label}</span>
                      </div>
                      {locked && <SafeIcon icon={FiLock} className="w-4 h-4" />}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tab Content */}
        <motion.div key={activeTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {activeTab === 'overview' && <StatsOverview />}
          {activeTab === 'timeline' && (
            <FeatureGate feature="Timeline Tracker">
              <TimelineTracker />
            </FeatureGate>
          )}
          {activeTab === 'colleges' && (
            <FeatureGate feature="College Tracker">
              <CollegeTracker />
            </FeatureGate>
          )}
          {activeTab === 'deadlines' && (
            <FeatureGate feature="Scholarship Deadlines">
              <ScholarshipDeadlines />
            </FeatureGate>
          )}
          {activeTab === 'bodies' && (
            <FeatureGate feature="Governing Bodies">
              {renderGoverningBodiesContent()}
            </FeatureGate>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default Dashboard;